import React from 'react';
import {
  DeliveryDestination,
  DeliveryInformationData,
  DeliveryProvider,
} from '../Rest/Services/UserService/Data';
import DeliveryProviderIcon from './DeliveryProviderIcon';

export const emptyDelivery = (overrides: Partial<DeliveryInformationData> = {}): DeliveryInformationData => ({
  name: '',
  lastName: '',
  phone: '',
  provider: DeliveryProvider.NovaPost,
  destination: DeliveryDestination.Department,
  city: '',
  number: '',
  displayName: '',
  countryCode: 'UA',
  isPrimary: true,
  novaPoshtaAddress: null,
  ukrPoshtaAddress: null,
  ...overrides,
});

export const providerLabel: Record<DeliveryProvider, string> = {
  [DeliveryProvider.NovaPost]: 'Нова пошта',
  [DeliveryProvider.UkrPost]: 'Укрпошта',
  [DeliveryProvider.Self]: 'Самовивіз',
};

export const destinationLabel: Record<DeliveryDestination, string> = {
  [DeliveryDestination.Department]: 'Відділення',
  [DeliveryDestination.ParcelLocker]: 'Поштомат',
  [DeliveryDestination.Address]: "Кур'єр",
};

export const renderProviderValue = (selected: unknown) => {
  const provider = String(selected) as DeliveryProvider;

  return (
    <span className="provider-select-value">
      <DeliveryProviderIcon provider={provider} />
      <span>{providerLabel[provider] ?? provider}</span>
    </span>
  );
};

export const getDeliveryDisplayName = (delivery: DeliveryInformationData) =>
  delivery.provider === DeliveryProvider.Self
    ? 'Самовивіз'
    : delivery.displayName?.trim() || [delivery.city, delivery.number].filter(Boolean).join(', ');

export const clearProviderDetails = (delivery: DeliveryInformationData): DeliveryInformationData => ({
  ...delivery,
  city: '',
  number: '',
  displayName: '',
  regionName: null,
  districtName: null,
  novaPoshtaAddress: null,
  ukrPoshtaAddress: null,
});

export const clearDeliveryTarget = (
  delivery: DeliveryInformationData,
  destination: DeliveryDestination
): DeliveryInformationData => {
  if (delivery.provider !== DeliveryProvider.NovaPost) {
    return { ...delivery, destination };
  }

  const nova = delivery.novaPoshtaAddress;
  return {
    ...delivery,
    destination,
    number: '',
    displayName: '',
    novaPoshtaAddress: nova
      ? {
          cityRef: nova.cityRef,
          settlementRef: nova.settlementRef,
          cityName: nova.cityName,
          areaName: nova.areaName,
          regionName: nova.regionName,
          settlementType: nova.settlementType,
        }
      : null,
  };
};

export const toDeliveryPayload = (delivery: DeliveryInformationData): Omit<DeliveryInformationData, 'id'> => ({
  label: delivery.label?.trim() || null,
  name: delivery.name.trim(),
  lastName: delivery.lastName.trim(),
  phone: delivery.phone.trim(),
  provider: delivery.provider,
  destination: delivery.provider === DeliveryProvider.Self ? DeliveryDestination.Address : delivery.destination,
  city: delivery.provider === DeliveryProvider.Self ? '' : delivery.city.trim(),
  number: delivery.provider === DeliveryProvider.Self ? '' : delivery.number.trim(),
  displayName: getDeliveryDisplayName(delivery),
  countryCode: delivery.countryCode || 'UA',
  regionName: delivery.regionName || null,
  districtName: delivery.districtName || null,
  isPrimary: delivery.isPrimary,
  novaPoshtaAddress: delivery.provider === DeliveryProvider.NovaPost ? delivery.novaPoshtaAddress ?? null : null,
  ukrPoshtaAddress: delivery.provider === DeliveryProvider.UkrPost ? delivery.ukrPoshtaAddress ?? null : null,
});
